export type Bounds = [[number, number], [number, number]];

export const BOUNDS_BUFFER_DEGREES = 0.01;

export interface BoundaryPoint {
  latitude: number;
  longitude: number;
}

/** Closed or open ring of [longitude, latitude] positions. */
export type LngLatRing = ReadonlyArray<readonly [number, number]>;

export interface ScreenPoint {
  x: number;
  y: number;
}

/** Minimal map surface needed to project lng/lat into screen pixels. */
export interface MapViewportProjector {
  project: (lngLat: [number, number]) => ScreenPoint;
}

/** Minimal map surface needed for padded rendered-feature queries. */
export interface MapRenderedFeatureQuery {
  getLayer: (id: string) => unknown;
  queryRenderedFeatures: (
    geometry: [[number, number], [number, number]],
    options: { layers: string[] },
  ) => unknown[];
}

/** Point at fraction `t` from `start` toward `end`. `t` is not clamped. */
export const pointAlongSegment = (
  start: BoundaryPoint,
  end: BoundaryPoint,
  t: number,
): BoundaryPoint => ({
  latitude: start.latitude + (end.latitude - start.latitude) * t,
  longitude: start.longitude + (end.longitude - start.longitude) * t,
});

/** Squared Euclidean distance in lng/lat degrees (avoids sqrt for comparisons). */
export const distanceSq = (a: BoundaryPoint, b: BoundaryPoint): number => {
  const dLng = a.longitude - b.longitude;
  const dLat = a.latitude - b.latitude;
  return dLng * dLng + dLat * dLat;
};

/**
 * Axis-aligned bounds of GPS coordinates. Expands a single-point set by
 * BOUNDS_BUFFER_DEGREES so fitBounds still has a usable area.
 */
export const getBoundsFromCoordinates = (
  coords: Array<Pick<BoundaryPoint, "latitude" | "longitude">>,
): Bounds | undefined => {
  if (coords.length === 0) {
    return undefined;
  }

  const lats = coords.map((coordinate) => coordinate.latitude);
  const lngs = coords.map((coordinate) => coordinate.longitude);
  let minLat = Math.min(...lats);
  let maxLat = Math.max(...lats);
  let minLng = Math.min(...lngs);
  let maxLng = Math.max(...lngs);

  // Expand a degenerate box so fitBounds still has a usable area.
  if (minLat === maxLat && minLng === maxLng) {
    minLat -= BOUNDS_BUFFER_DEGREES;
    maxLat += BOUNDS_BUFFER_DEGREES;
    minLng -= BOUNDS_BUFFER_DEGREES;
    maxLng += BOUNDS_BUFFER_DEGREES;
  }

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
};
